import type { LucideIcon } from 'lucide-react';
import {
    Activity,
    AlertTriangle,
    Ban,
    Building2,
    CreditCard,
    Database,
    FileText,
    Fingerprint,
    Globe2,
    Layers,
    Link2,
    Lock,
    MapPin,
    PenLine,
    Phone,
    Scale,
    Search,
    Shield,
    Shuffle,
    Skull,
    Target,
    Users,
} from 'lucide-react';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'unknown';

export type IntelStatTone = 'neutral' | 'brand' | 'warning' | 'danger';

export type CategoryVisual = {
    icon: LucideIcon;
    tileClass: string;
    iconClass: string;
};

const CHAIN_ALIASES: Record<string, string> = {
    ETHEREUM: 'ETH',
    ETH: 'ETH',
    TRON: 'TRX',
    TRX: 'TRX',
    BITCOIN: 'BTC',
    BTC: 'BTC',
    POLYGON: 'MATIC',
    MATIC: 'MATIC',
    BINANCE: 'BNB',
    BSC: 'BNB',
    BNB: 'BNB',
};

export function normalizeChainLabel(raw?: string | null): string {
    if (!raw) return 'ETH';
    const value = raw.trim().toUpperCase();
    return CHAIN_ALIASES[value] || value;
}

export function deriveSourceMonogram(source?: string | null): string {
    if (!source) return '??';
    const cleaned = source.replace(/[^a-zA-Z0-9]/g, ' ').trim();
    const parts = cleaned.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    const word = parts[0] || source;
    return word.slice(0, 2).toUpperCase();
}

export function resolveSeverityLevel(risk?: string | null): SeverityLevel {
    const value = (risk ?? '').toLowerCase().trim();
    if (!value) return 'unknown';
    if (value === 'critical' || value === 'phishing') return 'critical';
    if (value === 'high') return 'high';
    if (value === 'medium') return 'medium';
    if (value === 'low') return 'low';
    return 'unknown';
}

export function scoreToSeverity(score: number): SeverityLevel {
    if (score >= 0.85) return 'critical';
    if (score >= 0.7) return 'high';
    if (score >= 0.4) return 'medium';
    return 'low';
}

export const SEVERITY_STYLES: Record<SeverityLevel, { bar: string; bg: string; text: string }> = {
    critical: {
        bar: 'bg-red-600 dark:bg-red-500',
        bg: 'bg-red-500/10 border-red-500/25',
        text: 'text-red-700 dark:text-red-400',
    },
    high: {
        bar: 'bg-red-500 dark:bg-red-400',
        bg: 'bg-red-500/10 border-red-500/20',
        text: 'text-red-700 dark:text-red-400',
    },
    medium: {
        bar: 'bg-amber-500 dark:bg-amber-400',
        bg: 'bg-amber-500/10 border-amber-500/20',
        text: 'text-amber-700 dark:text-amber-400',
    },
    low: {
        bar: 'bg-emerald-500 dark:bg-emerald-400',
        bg: 'bg-emerald-500/10 border-emerald-500/20',
        text: 'text-emerald-700 dark:text-emerald-400',
    },
    unknown: {
        bar: 'bg-muted-foreground/50',
        bg: 'bg-muted/60 border-border',
        text: 'text-muted-foreground',
    },
};

export const SEVERITY_LABELS: Record<SeverityLevel, string> = {
    critical: 'CRITICAL',
    high: 'HIGH',
    medium: 'MEDIUM',
    low: 'LOW',
    unknown: 'UNKNOWN',
};

export function getCategoryVisual(category?: string | null): CategoryVisual {
    switch ((category ?? '').toLowerCase()) {
        case 'exchange':
            return {
                icon: Building2,
                tileClass: 'bg-primary/12 border-primary/20',
                iconClass: 'text-primary',
            };
        case 'scam':
        case 'phishing':
            return {
                icon: AlertTriangle,
                tileClass: 'bg-destructive/10 border-destructive/20',
                iconClass: 'text-destructive',
            };
        case 'mixer':
            return {
                icon: Shuffle,
                tileClass: 'bg-amber-500/12 border-amber-500/25',
                iconClass: 'text-amber-700 dark:text-amber-400',
            };
        case 'ransomware':
            return {
                icon: Skull,
                tileClass: 'bg-destructive/10 border-destructive/20',
                iconClass: 'text-destructive',
            };
        case 'sanction':
            return {
                icon: Ban,
                tileClass: 'bg-destructive/10 border-destructive/20',
                iconClass: 'text-destructive',
            };
        case 'court':
            return {
                icon: Scale,
                tileClass: 'bg-primary/12 border-primary/20',
                iconClass: 'text-primary',
            };
        default:
            return {
                icon: Shield,
                tileClass: 'bg-muted/70 border-border',
                iconClass: 'text-muted-foreground',
            };
    }
}

/** Harvest identifier types → category tile mapping. */
export function getIdentifierTypeVisual(idType?: string | null): CategoryVisual {
    switch ((idType ?? '').toLowerCase()) {
        case 'phone':
            return {
                icon: Phone,
                tileClass: 'bg-sky-500/10 border-sky-500/20',
                iconClass: 'text-sky-700 dark:text-sky-400',
            };
        case 'wallet':
        case 'address':
            return {
                icon: Link2,
                tileClass: 'bg-violet-500/10 border-violet-500/20',
                iconClass: 'text-violet-700 dark:text-violet-400',
            };
        case 'upi':
            return {
                icon: CreditCard,
                tileClass: 'bg-emerald-500/10 border-emerald-500/20',
                iconClass: 'text-emerald-700 dark:text-emerald-400',
            };
        case 'domain':
            return {
                icon: Globe2,
                tileClass: 'bg-orange-500/10 border-orange-500/20',
                iconClass: 'text-orange-700 dark:text-orange-400',
            };
        default:
            return {
                icon: Fingerprint,
                tileClass: 'bg-muted/70 border-border',
                iconClass: 'text-muted-foreground',
            };
    }
}

export const CHAIN_STYLES: Record<string, { tile: string; text: string }> = {
    ETH: { tile: 'bg-slate-500/12 border-slate-500/25', text: 'text-slate-700 dark:text-slate-300' },
    TRX: { tile: 'bg-red-500/10 border-red-500/20', text: 'text-red-700 dark:text-red-400' },
    BTC: { tile: 'bg-orange-500/10 border-orange-500/20', text: 'text-orange-700 dark:text-orange-400' },
    MATIC: { tile: 'bg-violet-500/10 border-violet-500/20', text: 'text-violet-700 dark:text-violet-400' },
    BNB: { tile: 'bg-amber-500/10 border-amber-500/20', text: 'text-amber-700 dark:text-amber-400' },
};

export function getChainStyle(chain?: string | null) {
    const label = normalizeChainLabel(chain);
    return CHAIN_STYLES[label] ?? {
        tile: 'bg-muted/70 border-border',
        text: 'text-foreground/80',
    };
}

export type SourceIconMatch = {
    icon: LucideIcon;
    monogram?: string;
};

export function resolveSourceVisual(source?: string | null): SourceIconMatch {
    const monogram = deriveSourceMonogram(source);
    if (!source) return { icon: Database, monogram: '??' };
    const s = source.toLowerCase();
    if (s.includes('opensanctions') || s.includes('ecourts') || s.includes('court')) {
        return { icon: Scale, monogram: 'OS' };
    }
    if (s.includes('ofac') || s.includes('sanction')) return { icon: Ban, monogram: 'OF' };
    if (s.includes('graphsense')) return { icon: Globe2, monogram: 'GS' };
    if (s.includes('chainabuse') || s.includes('scam')) return { icon: AlertTriangle, monogram: 'CA' };
    if (s.includes('walletexplorer')) return { icon: Search, monogram: 'WE' };
    if (s.includes('dune')) return { icon: Activity, monogram: 'DU' };
    if (s.includes('tether')) return { icon: Lock, monogram: 'TE' };
    if (s.includes('ransom')) return { icon: Skull, monogram: 'RA' };
    if (s.includes('etherscan') || s.includes('tronscan')) return { icon: FileText, monogram: 'EX' };
    if (s.includes('manual')) return { icon: PenLine, monogram: 'MN' };
    if (s.includes('exploit')) return { icon: Target, monogram: 'EX' };
    if (s.includes('moralis')) return { icon: Database, monogram: 'MO' };
    if (s.includes('telegram')) return { icon: Users, monogram: 'TG' };
    if (s.includes('p2p')) return { icon: Link2, monogram: 'P2' };
    if (s.includes('instagram')) return { icon: MapPin, monogram: 'IG' };
    return { icon: Database, monogram };
}

export const INTEL_STAT_TONE_CLASSES: Record<IntelStatTone, string> = {
    neutral: 'text-muted-foreground bg-muted/80',
    brand: 'text-primary bg-primary/10',
    warning: 'text-amber-700 dark:text-amber-400 bg-amber-500/10',
    danger: 'text-destructive bg-destructive/10',
};

/** Telegram VDA provider category badge styles (border + text). */
export const PROVIDER_CATEGORY_STYLES: Record<string, { border: string; text: string; bg: string }> = {
    fiu_onshore: {
        border: 'border-emerald-500/35',
        text: 'text-emerald-700 dark:text-emerald-400',
        bg: 'bg-emerald-500/8',
    },
    fiu_offshore: {
        border: 'border-blue-500/35',
        text: 'text-blue-700 dark:text-blue-400',
        bg: 'bg-blue-500/8',
    },
    offshore: {
        border: 'border-amber-500/35',
        text: 'text-amber-700 dark:text-amber-400',
        bg: 'bg-amber-500/8',
    },
    local_p2p: {
        border: 'border-primary/35',
        text: 'text-amber-800 dark:text-primary',
        bg: 'bg-primary/8',
    },
};

export const FIU_STATUS_TO_SEVERITY: Record<string, SeverityLevel> = {
    registered: 'low',
    show_cause: 'medium',
    pending: 'medium',
    unregistered: 'unknown',
    banned: 'critical',
};

export const FIU_STATUS_LABELS: Record<string, string> = {
    registered: 'REGISTERED',
    show_cause: 'SHOW CAUSE',
    pending: 'PENDING',
    unregistered: 'UNREGISTERED',
    banned: 'BANNED',
};
